"use client";

import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { getFacilitiesHomeList } from "@/data/facilities";
import { useLanguage, useTranslations } from "@/lib/i18n";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

export default function Facilities() {
  const { language } = useLanguage();
  const t = useTranslations();
  const facilitiesData = getFacilitiesHomeList(language);

  return (
    <section id="facilities" className="py-24 md:py-32 bg-white relative">
      <div className="container mx-auto px-6 md:px-12">
        {/* HEADER */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4">
              {t.facilities.tagline}
            </span>
            <h2 className="font-serif text-4xl md:text-5xl text-brand-charcoal mb-6 leading-tight whitespace-pre-line">
              {t.facilities.title}
            </h2>
            <p className="text-gray-500 font-light text-base md:text-lg leading-relaxed">
              {t.facilities.subtitle}
            </p>
          </motion.div>
        </div>

        {/* GRID FASILITAS */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16"
        >
          {facilitiesData.map((item) => (
            <motion.div
              key={item.id}
              variants={itemVariants}
              className="group bg-[#FAFAFA] hover:bg-white border border-transparent hover:border-brand-gold/20 p-6 md:p-8 lg:p-10 transition-all duration-500 hover:shadow-xl hover:-translate-y-1 rounded-sm overflow-hidden"
            >
              {/* Icon Header */}
              <div className="flex items-center justify-between mb-4 md:mb-6">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-white border border-gray-100 flex items-center justify-center text-brand-gold group-hover:bg-brand-gold group-hover:text-white transition-all duration-500 shadow-sm shrink-0">
                  <item.icon strokeWidth={1} className="w-5 h-5 md:w-6 md:h-6" />
                </div>
                <div className="h-[1px] flex-1 max-w-8 bg-gray-200 group-hover:bg-brand-gold/50 transition-colors ml-4" />
              </div>

              <h3 className="font-serif text-lg md:text-xl text-brand-charcoal mb-2 md:mb-3 group-hover:text-brand-gold transition-colors line-clamp-2 break-words">
                {item.title}
              </h3>

              <p className="text-gray-500 text-xs md:text-sm font-light leading-relaxed group-hover:text-gray-600 line-clamp-3">
                {item.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* BUTTON CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="text-center"
        >
          <Link href="/facilities">
            <Button
              variant="outline"
              className="rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white px-10 py-6 text-xs font-bold tracking-[0.2em] uppercase transition-all duration-300"
            >
              {t.facilities.exploreAll}
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
